/**
 * Per-domain brand configuration.
 *
 * The SPA reads `window.location.hostname` at boot and applies the matching
 * brand: page title, favicon, CSS color variables, and a couple of fields the
 * UI surfaces (name, tagline, source slug used when signing up for attribution).
 *
 * v1 keeps this hardcoded for velocity. v1.1 will read from the Sidequester
 * `sites` table via a public endpoint so adding a new domain doesn't require
 * a redeploy.
 *
 * To add a new site here:
 *   1. Add a `Brand` entry keyed by the bare host (no www).
 *   2. Make sure a row exists in Sidequester `sites` with `domain` matching.
 *   3. Update the Bloxx Worker's wrangler.toml + cms_pages for `/dashboard`.
 */

export interface Brand {
  /** Public site name shown in the header / page title. */
  name: string;
  /** Short tagline used on the dashboard landing CTA. */
  tagline: string;
  /** Headline copy on the marketing landing page. */
  hero: string;
  /** The slug we pass to the Sidequester signup endpoint for attribution.
   *  Should match a `sites.domain` row in Sidequester. */
  sourceSlug: string;
  /** HSL string (no `hsl()` wrapper) — written onto --primary at runtime. */
  primary: string;
  /** Where the dashboard's "About this site" link points to. */
  marketingUrl: string;
}

const FALLBACK_BRAND: Brand = {
  name: "Bloxx Dashboard",
  tagline: "Connect FreeAgent, see your numbers in 60 seconds.",
  hero: "Your personal CFO dashboard.",
  sourceSlug: "itsbloxx.com",
  primary: "222 47% 11%",
  marketingUrl: "https://itsbloxx.com",
};

const BRANDS: Record<string, Brand> = {
  "gocfo.co.uk": {
    name: "GoCFO",
    tagline: "Your numbers, on demand.",
    hero: "Connect FreeAgent, get an instant CFO dashboard.",
    sourceSlug: "gocfo.co.uk",
    primary: "221 83% 53%",
    marketingUrl: "https://gocfo.co.uk",
  },
  "golandlord.co.uk": {
    name: "GoLandlord",
    tagline: "Run your property business on autopilot.",
    hero: "See your portfolio numbers in 60 seconds.",
    sourceSlug: "golandlord.co.uk",
    primary: "142 71% 35%",
    marketingUrl: "https://golandlord.co.uk",
  },
  "goecom.co.uk": {
    name: "GoEcom",
    tagline: "Your store's margin, in plain English.",
    hero: "Connect FreeAgent and see your real profit.",
    sourceSlug: "goecom.co.uk",
    primary: "262 83% 58%",
    marketingUrl: "https://goecom.co.uk",
  },
  "gocowork.co.uk": {
    name: "GoCowork",
    tagline: "Cowork-friendly bookkeeping.",
    hero: "Your numbers, simplified.",
    sourceSlug: "gocowork.co.uk",
    primary: "346 77% 50%",
    marketingUrl: "https://gocowork.co.uk",
  },
  "gocreatives.co.uk": {
    name: "GoCreatives",
    tagline: "Built for creative businesses.",
    hero: "See your numbers without the admin headache.",
    sourceSlug: "gocreatives.co.uk",
    primary: "27 96% 61%",
    marketingUrl: "https://gocreatives.co.uk",
  },
};

function normaliseHost(host: string): string {
  return host.toLowerCase().replace(/^www\./, "");
}

export function brandForHost(host: string): Brand {
  return BRANDS[normaliseHost(host)] ?? FALLBACK_BRAND;
}

/** Applied once at app boot from main.tsx. */
export function applyBrandToDocument(brand: Brand) {
  if (typeof document === "undefined") return;
  document.documentElement.style.setProperty("--primary", brand.primary);
  document.title = brand.name;
}

export function currentBrand(): Brand {
  if (typeof window === "undefined") return FALLBACK_BRAND;
  return brandForHost(window.location.hostname);
}
